import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Grid2X2, List, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, resolveAssetUrl } from "@/lib/api";
import { SAUDE, STATUS_CLIENTE, FREQ_CAPTACAO } from "@/lib/helpers";
import { CLIENT_GROUPS, grupoDoCliente, tipoCliente } from "@/lib/clientes";
import { useClientListPreferences } from "@/hooks/useClientListPreferences";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EditingFields } from "@/components/EditingSchedule";

const empty = { nome: "", empresa: "", email: "", whatsapp: "", tipo: "avulso", rotina: "", entregas_mes: 0, drive_link: "", instagram: "", site: "", responsavel_interno: "", sla: "", logo: "", data_inicio: "", frequencia_captacao: "mensal", status: "ativo", notas: "" };

function ClienteCard({ cliente: c, index, onOpen }) {
  const st = STATUS_CLIENTE[c.status] || STATUS_CLIENTE.ativo;
  const sd = SAUDE[c.saude] || SAUDE.saudavel;
  return (
    <motion.button key={c.id} data-testid={`cliente-card-${c.id}`} onClick={onOpen}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.04, 0.4) }}
      className="studio-card studio-card-hover p-5 text-left">
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-full bg-[#8CC63F]/12 border border-[#8CC63F]/30 flex items-center justify-center font-serif text-[#8CC63F] overflow-hidden">
          {c.logo ? <img src={resolveAssetUrl(c.logo)} alt={c.empresa} className="w-full h-full object-cover" /> : c.empresa[0]}
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sd.cor }} title={`Saúde: ${sd.nome}`} />
          <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border" style={{ color: st.cor, borderColor: `${st.cor}55` }}>{st.nome}</span>
        </div>
      </div>
      <p className="text-base font-medium text-[#F4F4F0] mt-4">{c.empresa}</p>
      <p className="caption-label mt-1 normal-case tracking-normal">{tipoCliente(c)}</p>
      <div className="mt-4 pt-4 border-t border-[#27272A]">
        <p className={`text-[11px] truncate ${c.proxima_acao ? "text-[#A1A1AA]" : "text-[#71717A]"}`}>
          {c.proxima_acao ? `→ ${c.proxima_acao.titulo}` : "Sem próxima ação definida"}
        </p>
        <div className="flex items-center justify-between mt-2">
          <span className="font-mono text-[10px] text-[#71717A] uppercase">{c.projetos_ativos} ciclo(s) ativo(s)</span>
          <span className="font-mono text-[10px] text-[#71717A] uppercase">{c.tarefas_abertas} tarefa(s)</span>
        </div>
      </div>
    </motion.button>
  );
}

function ClienteRow({ cliente: c, index, onOpen }) {
  const st = STATUS_CLIENTE[c.status] || STATUS_CLIENTE.ativo;
  const sd = SAUDE[c.saude] || SAUDE.saudavel;
  return (
    <motion.button data-testid={`cliente-row-${c.id}`} onClick={onOpen}
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.025, 0.25) }}
      className="studio-card studio-card-hover w-full px-4 py-3 text-left grid gap-3 items-center md:grid-cols-[minmax(220px,1.4fr)_minmax(150px,1fr)_minmax(200px,1.5fr)_110px_100px]">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-full bg-[#8CC63F]/12 border border-[#8CC63F]/30 flex items-center justify-center font-serif text-[#8CC63F] overflow-hidden shrink-0">
          {c.logo ? <img src={resolveAssetUrl(c.logo)} alt={c.empresa} className="w-full h-full object-cover" /> : c.empresa[0]}
        </div>
        <div className="min-w-0"><p className="text-sm font-medium text-[#F4F4F0] truncate">{c.empresa}</p><p className="caption-label normal-case tracking-normal truncate">{tipoCliente(c)}</p></div>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: sd.cor }} title={`Saúde: ${sd.nome}`} />
        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: st.cor }}>{st.nome}</span>
      </div>
      <p className="text-xs text-[#A1A1AA] truncate">{c.proxima_acao ? `→ ${c.proxima_acao.titulo}` : "Sem próxima ação definida"}</p>
      <span className="font-mono text-[10px] text-[#71717A] uppercase">{c.projetos_ativos} ciclo(s)</span>
      <span className="font-mono text-[10px] text-[#71717A] uppercase">{c.tarefas_abertas} tarefa(s)</span>
    </motion.button>
  );
}

export default function Clientes({ embedded = false }) {
  const [clientes, setClientes] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const { grupo, setGrupo, visualizacao, setVisualizacao } = useClientListPreferences();
  const navigate = useNavigate();

  const load = () => api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const salvar = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/clientes", { ...form, entregas_mes: parseInt(form.entregas_mes) || 0 });
      toast.success("Cliente cadastrado");
      setDialog(false);
      setForm(empty);
      navigate(`/producao/clientes/${res.data.id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const clientesFiltrados = clientes.filter((cliente) => grupoDoCliente(cliente) === grupo);
  const totais = clientes.reduce((acc, cliente) => {
    const chave = grupoDoCliente(cliente);
    acc[chave] += 1;
    return acc;
  }, { recorrentes: 0, one_time: 0, onboarding: 0, encerrados: 0 });
  const grupoAtual = CLIENT_GROUPS.find((item) => item.key === grupo) || CLIENT_GROUPS[0];

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {!embedded && <p className="caption-label mb-2">Clientes</p>}
          {embedded ? (
            <>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#F4F4F0] tracking-tight">Clientes da <span className="gold-italic">produção</span></h2>
              <p className="mt-1 text-sm text-[#71717A]">Cadastros e visão operacional no mesmo lugar.</p>
            </>
          ) : (
            <h1 className="font-serif text-3xl sm:text-4xl text-[#F4F4F0] tracking-tight">Carteira de <span className="gold-italic">Clientes</span></h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full border border-[#27272A] bg-[#141418] p-1" aria-label="Modo de visualização">
            <button type="button" aria-label="Visualizar em cartões" aria-pressed={visualizacao === "cards"} onClick={() => setVisualizacao("cards")}
              className={`p-2 rounded-full transition-colors ${visualizacao === "cards" ? "bg-[#8CC63F] text-[#09090B]" : "text-[#71717A] hover:text-[#F4F4F0]"}`}><Grid2X2 size={15} /></button>
            <button type="button" aria-label="Visualizar em lista" aria-pressed={visualizacao === "lista"} onClick={() => setVisualizacao("lista")}
              className={`p-2 rounded-full transition-colors ${visualizacao === "lista" ? "bg-[#8CC63F] text-[#09090B]" : "text-[#71717A] hover:text-[#F4F4F0]"}`}><List size={16} /></button>
          </div>
          <Button data-testid="novo-cliente-button" onClick={() => setDialog(true)} className="flex-1 bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold sm:flex-none">
            <Plus size={16} className="mr-1" /> Novo cliente
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-1 overflow-x-auto border-b border-[#27272A]" role="tablist" aria-label="Tipo dos clientes">
        {CLIENT_GROUPS.map((item) => (
          <button key={item.key} type="button" role="tab" data-testid={`clientes-grupo-${item.key}`} aria-selected={grupo === item.key} onClick={() => setGrupo(item.key)}
            className={`-mb-px shrink-0 border-b-2 px-4 py-3 text-sm transition-colors ${grupo === item.key ? "border-[#8CC63F] text-[#F4F4F0]" : "border-transparent text-[#71717A] hover:text-[#A1A1AA]"}`}>
            {item.nome} <span className="font-mono text-[10px] ml-1">{totais[item.key]}</span>
          </button>
        ))}
      </div>

      <div className={visualizacao === "cards" ? "grid sm:grid-cols-2 xl:grid-cols-3 gap-4" : "space-y-2"}>
        {clientesFiltrados.map((c, i) => visualizacao === "cards"
          ? <ClienteCard key={c.id} cliente={c} index={i} onOpen={() => navigate(`/producao/clientes/${c.id}`)} />
          : <ClienteRow key={c.id} cliente={c} index={i} onOpen={() => navigate(`/producao/clientes/${c.id}`)} />)}
      </div>
      {clientesFiltrados.length === 0 && <p className="text-sm text-[#71717A]">{grupoAtual.vazio}</p>}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Novo <span className="gold-italic">cliente</span></DialogTitle></DialogHeader>
          <form onSubmit={salvar} className="space-y-4 mt-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Empresa</Label>
                <Input data-testid="cliente-empresa-input" required value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Contato principal</Label>
                <Input data-testid="cliente-nome-input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="cliente-tipo-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    <SelectItem value="avulso">One time</SelectItem>
                    <SelectItem value="recorrente">Recorrente</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="caption-label">Frequência de captação</Label>
                <Select value={form.frequencia_captacao} onValueChange={(v) => setForm({ ...form, frequencia_captacao: v })}>
                  <SelectTrigger data-testid="cliente-freq-select" className="mt-1.5 bg-[#18181C] border-[#27272A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#18181C] border-[#27272A] text-[#E4E4E0]">
                    {FREQ_CAPTACAO.map((f) => <SelectItem key={f.key} value={f.key}>{f.nome}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label className="caption-label">WhatsApp</Label>
                <Input data-testid="cliente-whatsapp-input" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" /></div>
              <div><Label className="caption-label">Instagram</Label>
                <Input data-testid="cliente-instagram-input" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="@perfil" /></div>
            </div>
            {form.tipo === "recorrente" && <EditingFields form={form} onChange={(key, value) => setForm({ ...form, [key]: value })} />}
            <div><Label className="caption-label">Serviços contratados / rotina</Label>
              <Input data-testid="cliente-rotina-input" value={form.rotina} onChange={(e) => setForm({ ...form, rotina: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" placeholder="Ex: 8 vídeos verticais, 3 posts/semana" /></div>
            <div><Label className="caption-label">Observações</Label>
              <Textarea data-testid="cliente-notas-input" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="mt-1.5 bg-[#18181C] border-[#27272A]" rows={2} /></div>
            <Button data-testid="cliente-submit-button" type="submit" className="w-full bg-[#8CC63F] text-[#09090B] hover:bg-[#A9DD6B] rounded-full font-semibold">Cadastrar cliente</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
